import React, { useState,  useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginService } from '../services/LoginService';
import { roleActions } from '../redux/Role';
import { useDispatch } from 'react-redux';
import StatusContext from './status';
import { getConfig } from '../helpers/config';

const AuthContext = React.createContext({
	updatePrivacy: async (privacy) => {},
	login: async (token, refreshToken, userInfo) => {},
	baseLogin: async (token) => {},
	refresh: async () => {},
	getUserInfo: async () => {},
	logout: async () => {},
	resetParams: async (toNavigate) => {},
	refreshTokens: (accessToken, refreshToken, userInfo) => {},
	refreshOnlyTokens: (accessToken, refreshToken) => {},
	forceLogout: (toNavigate) => {},
	clearSessionStorage: (allData) => {},
	removeRoleAndEnteSelected: () => {}
});

export const AuthContextProvider = ({ children }) => {
	const [token, setToken] = useState(sessionStorage.getItem('access_token'));
	const [userInfo, setUserInfo] = useState(JSON.parse(sessionStorage.getItem('user_info')));
	const environment = getConfig();
	const navigate = useNavigate();
  const dispatch = useDispatch();

  const statusContext = useContext(StatusContext);

	const loginService = new LoginService();

	const baseLoginHandler = (token, userInfo) => {
		setUserInfo(userInfo);
		setToken(token);
		sessionStorage.setItem('user_info', JSON.stringify(userInfo));
		sessionStorage.setItem('access_token', token);
		return token;
	}

	const loginHandler = async (token, refreshToken, userInfo) => {
		sessionStorage.removeItem('role_selected');
		sessionStorage.removeItem('ente_selected');
		setUserInfo(userInfo);
		setToken(token);
		sessionStorage.setItem('user_info', JSON.stringify(userInfo));
		sessionStorage.setItem('access_token', token);
		sessionStorage.setItem('refresh_token', refreshToken);
		return token;
	};

	const refreshHandler = async () => {
		return await loginService.refresh();
	}

	const logoutHandler = async () => {
		const checkEvent = await statusContext.checkEvent('onbeforeunload');
		if (
			checkEvent &&
			!window.confirm('Le modifiche apportate potrebbero non essere salvate.')
		) {
			return;
		}

		statusContext.removeEvent('onbeforeunload');

		const token = sessionStorage.getItem('access_token');
		const removeTokenData = true;
		const toNavigate = environment?.profile === 'dev' || environment?.profile === 'staging';
		if (environment?.profile !== 'dev') {
			console.log('chiamo logout');
			await logout(token, removeTokenData, toNavigate);
		} else {
			standardLogout(removeTokenData);
		}
		console.log('auth.logout, toNavigate',toNavigate);
		if (toNavigate) {
			//navigate(environment?.loginPath);			
			console.log('navigo verso mainpage');
			window.location.href = environment?.mainPage;
		}
		if (environment?.profile === 'rt_test' || environment?.profile === 'prod') {
			console.log('auth.logout, mainPage', environment?.mainPage);
			window.location.href = environment?.mainPage;
		}
	};

	const getUserInfoHandler = async (token) => {
		return await loginService.getUserInfo(token);
	}

	async function logout(token, removeTokenData, toNavigate) {
		try {
			await loginService.logout(token, toNavigate)
				.then(() => {
					completeLogout(removeTokenData);
				}).catch(error => {
					console.log(error);
					completeLogout(removeTokenData);
				});
		} catch (error) {
			console.log(`errore: ${error}`);
			throw new Error('Errore logout');
		}
	}

	const completeLogout = (removeTokenData) => {
		clearSessionStorage(removeTokenData);

		if (removeTokenData) {
			setToken(null);
			clearRoles();
		}
	}

	const standardLogout = (removeTokenData) => {
		if (removeTokenData) {
			setToken(null);
			clearRoles();
		}
		clearSessionStorage(removeTokenData);
	}

	const clearSessionStorage = (removeRoleSelected = true) => {
        //sessionStorage.clear();
		sessionStorage.removeItem('access_token');
		sessionStorage.removeItem('refresh_token');
		sessionStorage.removeItem('user_info');
		sessionStorage.removeItem('state');
		if (removeRoleSelected) {
			sessionStorage.removeItem('role_selected');
			sessionStorage.removeItem('ente_selected');
		}
	}

	const clearRoles = () => {
    dispatch(roleActions.setRoleSelected(null));
    dispatch(roleActions.setEnteSelected(null));
		dispatch(roleActions.setRoles(null));
	}

	const resetParamsHandler = (toNavigate) => {
		setToken(null);
		sessionStorage.removeItem('role_selected');
		sessionStorage.removeItem('ente_selected');
		clearRoles();

		if (toNavigate) {
			navigate(environment?.loginPath);			
		}
	}

	const refreshTokensHandler = (accessToken, refreshToken, userInfo) => {
		setUserInfo(userInfo);
		setToken(accessToken);
		sessionStorage.setItem('user_info', JSON.stringify(userInfo));
		sessionStorage.setItem('access_token', accessToken);
		sessionStorage.setItem('refresh_token', refreshToken);
	}

	const refreshOnlyTokensHandler = (accessToken, refreshToken) => {
		setToken(accessToken);
		sessionStorage.setItem('access_token', accessToken);
		sessionStorage.setItem('refresh_token', refreshToken);
	}

	const forceLogoutHandler = (toNavigate) => {
		setToken(null);
		clearSessionStorage();
		clearRoles();
		if (toNavigate) {
			navigate(environment?.loginPath);			
		}
	}

	const removeRoleAndEnteSelected = () => {
		sessionStorage.removeItem('role_selected');
		sessionStorage.removeItem('ente_selected');
	}

	const contextValue = useMemo(() => ({
		token: token,
		isLoggedIn: !!token,
		userInfo: userInfo,
		login: loginHandler,
		baseLogin: baseLoginHandler,
		refresh: refreshHandler,
		getUserInfo: getUserInfoHandler,
		logout: logoutHandler,
		resetParams: resetParamsHandler,
		refreshTokens: refreshTokensHandler,
		refreshOnlyTokens: refreshOnlyTokensHandler,
		forceLogout: forceLogoutHandler,
		clearSessionStorage: clearSessionStorage,
		removeRoleAndEnteSelected: removeRoleAndEnteSelected
	}), [token, userInfo]);

	return (
		<AuthContext.Provider value={contextValue}>
			{ children }
		</AuthContext.Provider>
	);
};

export default AuthContext;
